<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <div>
          <h3 class="text-xl font-bold text-gray-900">Manage Leave Types</h3>
          <p class="text-sm text-gray-600">{{ bank.name }}</p>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <X class="w-6 h-6" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 space-y-6">
        <!-- Currently Assigned Leave Types -->
        <div>
          <h4 class="text-sm font-medium text-gray-900 mb-3">Currently Assigned Leave Types</h4>
          <div v-if="loadingAssigned" class="text-center py-4 text-gray-500">Loading...</div>
          <div v-else-if="assignedLeaveTypes.length === 0" class="text-center py-8 text-gray-500">
            <List class="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No leave types assigned to this bank</p>
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="lt in assignedLeaveTypes"
              :key="lt.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: lt.color }"></div>
                <span class="font-medium">{{ lt.name }}</span>
                <span class="px-2 py-1 text-xs bg-gray-200 rounded">{{ lt.code }}</span>
              </div>
              <button
                @click="removeLeaveType(lt.id)"
                class="p-1 text-red-600 hover:bg-red-50 rounded"
                title="Remove from bank"
              >
                <X class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Available Leave Types -->
        <div>
          <h4 class="text-sm font-medium text-gray-900 mb-3">Available Leave Types</h4>
          <p class="text-xs text-gray-500 mb-3">
            These leave types are not assigned to any bank. Click to add them to this bank.
          </p>
          <div v-if="loadingAvailable" class="text-center py-4 text-gray-500">Loading...</div>
          <div v-else-if="availableLeaveTypes.length === 0" class="text-center py-4 text-gray-500">
            All leave types are assigned
          </div>
          <div v-else class="space-y-2">
            <button
              v-for="lt in availableLeaveTypes"
              :key="lt.id"
              @click="assignLeaveType(lt.id)"
              class="w-full flex items-center gap-3 p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50"
            >
              <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: lt.color }"></div>
              <span class="font-medium">{{ lt.name }}</span>
              <span class="px-2 py-1 text-xs bg-gray-200 rounded">{{ lt.code }}</span>
              <Plus class="w-4 h-4 ml-auto text-gray-400" />
            </button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {{ error }}
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { X, Plus, List } from 'lucide-vue-next';
import balanceBankService from '@/services/balanceBankService';
import apiClient from '@/services/api';

const props = defineProps({
  bank: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const loadingAssigned = ref(false);
const loadingAvailable = ref(false);
const error = ref(null);
const assignedLeaveTypes = ref([]);
const availableLeaveTypes = ref([]);
const allLeaveTypes = ref([]);

onMounted(async () => {
  await loadLeaveTypes();
});

async function loadLeaveTypes() {
  loadingAssigned.value = true;
  loadingAvailable.value = true;
  error.value = null;

  try {
    // Get all leave types
    const allTypesResponse = await apiClient.get('/admin/leave-types');
    allLeaveTypes.value = allTypesResponse.data.data || [];

    // Get assigned leave types for this bank
    const assignedResponse = await balanceBankService.getBankLeaveTypes(props.bank.id);
    assignedLeaveTypes.value = assignedResponse.data || [];

    // Filter available leave types (not assigned to any bank)
    const assignedIds = new Set(assignedLeaveTypes.value.map(lt => lt.id));

    // Get all banks to check which types are assigned elsewhere
    const banksResponse = await balanceBankService.getAllBanks();
    const allBanks = banksResponse.data || [];

    // Collect all assigned leave type IDs from all banks
    const allAssignedIds = new Set();
    for (const bank of allBanks) {
      if (bank.id !== props.bank.id) {
        const bankTypesResponse = await balanceBankService.getBankLeaveTypes(bank.id);
        const bankTypes = bankTypesResponse.data || [];
        bankTypes.forEach(lt => allAssignedIds.add(lt.id));
      }
    }

    // Available types are those not assigned to any bank (including this one)
    availableLeaveTypes.value = allLeaveTypes.value.filter(
      lt => !assignedIds.has(lt.id) && !allAssignedIds.has(lt.id) && lt.is_active !== 0
    );
  } catch (err) {
    console.error('Error loading leave types:', err);
    error.value = 'Failed to load leave types';
  } finally {
    loadingAssigned.value = false;
    loadingAvailable.value = false;
  }
}

async function assignLeaveType(leaveTypeId) {
  try {
    await balanceBankService.assignLeaveType(props.bank.id, leaveTypeId);
    await loadLeaveTypes();
    emit('saved');
  } catch (err) {
    console.error('Error assigning leave type:', err);
    error.value = err.response?.data?.message || 'Failed to assign leave type';
  }
}

async function removeLeaveType(leaveTypeId) {
  try {
    await balanceBankService.removeLeaveType(props.bank.id, leaveTypeId);
    await loadLeaveTypes();
    emit('saved');
  } catch (err) {
    console.error('Error removing leave type:', err);
    error.value = err.response?.data?.message || 'Failed to remove leave type';
  }
}
</script>
